import {
  Stack,
  Typography,
  TextField,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  Divider,
} from '@mui/material';
import { AddRounded, DeleteRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ProgramDocumentRequirement } from '../../../lib/api/academic.api';

interface ManageDocumentRequirementsProps {
  requirements: ProgramDocumentRequirement[];
  onChange: (requirements: ProgramDocumentRequirement[]) => void;
}

export function ManageDocumentRequirements({
  requirements,
  onChange,
}: ManageDocumentRequirementsProps) {
  const { t } = useTranslation('sis');

  const handleAdd = () => {
    onChange([...requirements, { type: '', required: true, description: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(requirements.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: keyof ProgramDocumentRequirement, value: any) => {
    const newReqs = [...requirements];
    const item = newReqs[index];
    if (!item) return;
    const { type, required, description } = item;

    if (field === 'type') {
      const next: ProgramDocumentRequirement = { type: value, required };
      if (description) next.description = description;
      newReqs[index] = next;
    } else if (field === 'required') {
      const next: ProgramDocumentRequirement = { type, required: value };
      if (description) next.description = description;
      newReqs[index] = next;
    } else if (field === 'description') {
      const nextItem: ProgramDocumentRequirement = { type, required };
      if (value) nextItem.description = value;
      newReqs[index] = nextItem;
    }

    onChange(newReqs);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('documentRequirements.heading')}
        </Typography>
        <Button
          size="small"
          startIcon={<AddRounded />}
          onClick={handleAdd}
          sx={{ color: 'primary.main' }}
        >
          {t('documentRequirements.addRequirement')}
        </Button>
      </Stack>

      <Stack spacing={2}>
        {requirements.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: 'rgba(255,255,255,0.02)',
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('documentRequirements.emptyState')}
            </Typography>
          </Paper>
        ) : (
          requirements.map((req, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, position: 'relative' }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    size="small"
                    label={t('documentRequirements.documentTypeLabel')}
                    placeholder={t('documentRequirements.documentTypePlaceholder')}
                    fullWidth
                    required
                    value={req.type}
                    onChange={(e) => handleChange(index, 'type', e.target.value)}
                  />
                  <IconButton size="small" color="error" onClick={() => handleRemove(index)}>
                    <DeleteRounded fontSize="small" />
                  </IconButton>
                </Stack>

                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <TextField
                    size="small"
                    label={t('documentRequirements.descriptionLabel')}
                    placeholder={t('documentRequirements.descriptionPlaceholder')}
                    fullWidth
                    value={req.description || ''}
                    onChange={(e) => handleChange(index, 'description', e.target.value)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={req.required}
                        onChange={(e) => handleChange(index, 'required', e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        {t('documentRequirements.requiredCheckbox')}
                      </Typography>
                    }
                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                  />
                </Stack>
              </Stack>
            </Paper>
          ))
        )}
      </Stack>
      <Divider sx={{ mt: 3 }} />
    </Box>
  );
}
